import { useEffect } from "react";
import { getDefaultSeo, type SeoLocale } from "../seo/config";

type KeywordsInput = string | string[] | undefined;

export interface SeoOptions {
  title?: string;
  description?: string;
  keywords?: KeywordsInput;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  locale?: SeoLocale;
}

const ensureMetaByName = (name: string) => {
  let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  return element;
};

const ensureMetaByProperty = (property: string) => {
  let element = document.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`,
  );
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  return element;
};

const ensureLinkRel = (rel: string) => {
  let element = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  return element;
};

export const useSeo = (overrides?: SeoOptions) => {
  useEffect(() => {
    const base = getDefaultSeo(overrides?.locale);

    const title = overrides?.title ?? base.title;
    const description = overrides?.description ?? base.description;

    let keywordList: string[] = base.keywords;
    if (Array.isArray(overrides?.keywords)) {
      keywordList = overrides?.keywords;
    } else if (typeof overrides?.keywords === "string") {
      keywordList = overrides.keywords
        .split(",")
        .map((keyword) => keyword.trim())
        .filter(Boolean);
    }

    const canonical = overrides?.canonical ?? base.canonical;
    const ogImage = overrides?.ogImage ?? base.ogImage;
    const robots = overrides?.noindex ? "noindex, nofollow" : "index, follow";

    document.title = title;

    ensureMetaByName("description").setAttribute("content", description);

    ensureMetaByName("keywords").setAttribute(
      "content",
      keywordList.join(", "),
    );

    ensureMetaByProperty("og:title").setAttribute("content", title);
    ensureMetaByProperty("og:description").setAttribute(
      "content",
      description,
    );

    if (ogImage) {
      ensureMetaByProperty("og:image").setAttribute("content", ogImage);
    }

    if (canonical) {
      ensureLinkRel("canonical").setAttribute("href", canonical);
    }

    ensureMetaByName("robots").setAttribute(
      "content",
      robots,
    );
  }, [
    overrides?.title,
    overrides?.description,
    Array.isArray(overrides?.keywords)
      ? overrides?.keywords.join(",")
      : overrides?.keywords,
    overrides?.canonical,
    overrides?.ogImage,
    overrides?.noindex,
    overrides?.locale,
  ]);
};
